import React, { useState, useEffect } from 'react';
import useGET from '../../services/useGET';
import { getToken } from '../../services/tokenGET';

export default function EditSelectedZoneModal({ 
    isOpen, 
    selectedZone,
    selectedZoneId,
    onClose,
    onChangeName,
    onRemove
}) {
    const [allSpecies, setAllSpecies] = useState([]);
    const [zoneSpecies, setZoneSpecies] = useState([]);
    const [selectedSpeciesIds, setSelectedSpeciesIds] = useState(new Set());
    const [areaImage, setAreaImage] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState("");
    const fileInputRef = React.useRef(null);
    const nameInputRef = React.useRef(null);
    
    const { data: fetchedSpecies } = useGET(`${process.env.REACT_APP_API_URL}/api/species`);
    const { data: fetchedZoneSpecies } = useGET(
        selectedZoneId ? `${process.env.REACT_APP_API_URL}/api/species/${selectedZoneId}` : null
    );

    useEffect(() => {
        if (fetchedSpecies) {
            setAllSpecies(fetchedSpecies);
        }
    }, [fetchedSpecies]);

    useEffect(() => {
        if (fetchedZoneSpecies) {
            setZoneSpecies(fetchedZoneSpecies);
            const speciesIds = new Set(fetchedZoneSpecies.map(s => s.id));
            setSelectedSpeciesIds(speciesIds);
        }
    }, [fetchedZoneSpecies]);

    useEffect(() => {
        if (selectedZone) {
            setEditedName(selectedZone);
        }
    }, [selectedZone]);

    useEffect(() => {
        if (isEditingName && nameInputRef.current) {
            nameInputRef.current.focus();
            nameInputRef.current.select();
        }
    }, [isEditingName]);

    // Fetch area image
    useEffect(() => {
        if (selectedZoneId) {
            fetch(`${process.env.REACT_APP_API_URL}/api/areas/${selectedZoneId}/images`)
                .then(response => {
                    if (!response.ok) {
                        console.warn(`No image found for area ${selectedZoneId}`);
                        setAreaImage(null);
                        return null;
                    }
                    return response.blob();
                })
                .then(blob => {
                    if (blob) {
                        const imageUrl = URL.createObjectURL(blob);
                        setAreaImage(imageUrl);
                    }
                })
                .catch(error => {
                    console.error('Error fetching area image:', error);
                    setAreaImage(null);
                });
        }
    }, [selectedZoneId]);

    const handleSpeciesToggle = (speciesId) => {
        setSelectedSpeciesIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(speciesId)) {
                newSet.delete(speciesId);
            } else {
                newSet.add(speciesId);
            }
            return newSet;
        });
    };

    const handleNameEditComplete = () => {
        if (!editedName.trim()) {
            setEditedName(selectedZone);
        }
        setIsEditingName(false);
    };

    const handleNameKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleNameEditComplete();
        } else if (e.key === 'Escape') {
            setEditedName(selectedZone);
            setIsEditingName(false);
        }
    };

    const handleSaveSpeciesList = async () => {
        if (!selectedZoneId) return;

        try {
            //const token = await getToken();
            const token = localStorage.getItem("token");
            let hasChanges = false;
            
            // 1. Update area name if changed
            if (editedName.trim() && editedName !== selectedZone) {
                // First, fetch the current area details to get description and publish_flag
                const areaDetailsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/areas`);
                const allAreas = await areaDetailsResponse.json();
                const currentArea = allAreas.find(a => a.id === selectedZoneId);
                
                if (!currentArea) {
                    throw new Error('Could not find area details');
                }
                
                const nameResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/areas/${selectedZoneId}`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ 
                        newName: editedName,
                        description: currentArea.description || '',
                        publish_flag: currentArea.publish_flag ?? true
                    })
                });

                if (nameResponse.status === 200) {
                    await onChangeName(editedName);
                    hasChanges = true;
                } else {
                    throw new Error('Failed to update area name');
                }
            }
            
            // 2. Update species list
            const currentZoneSpeciesIds = new Set(zoneSpecies.map(s => s.id));
            const speciesToAdd = Array.from(selectedSpeciesIds).filter(id => !currentZoneSpeciesIds.has(id));
            const speciesToRemove = Array.from(currentZoneSpeciesIds).filter(id => !selectedSpeciesIds.has(id));

            // Add new species
            if (speciesToAdd.length > 0) {
                await fetch(`${process.env.REACT_APP_API_URL}/api/species/areas/${selectedZoneId}/species`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ species_ids: speciesToAdd })
                });
                hasChanges = true;
            }

            // Remove species
            if (speciesToRemove.length > 0) {
                await fetch(`${process.env.REACT_APP_API_URL}/api/species/areas/${selectedZoneId}/species`, {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ species_ids: speciesToRemove })
                });
                hasChanges = true;
            }

            if (hasChanges) {
                alert('Ändringar har sparats!');
            } else {
                alert('Inga ändringar att spara.');
            }
            
            // Refresh zone species
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${selectedZoneId}`);
            const updatedSpecies = await response.json();
            setZoneSpecies(updatedSpecies);
        } catch (error) {
            console.error('Error saving changes:', error);
            alert('Ett fel uppstod när ändringarna skulle sparas.');
        }
    };

    // Group species by category
    const groupedSpecies = allSpecies.reduce((acc, species) => {
        const category = species.category_name || "Övrigt";
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(species);
        return acc;
    }, {});

    // Filter species by search query
    const filteredGroupedSpecies = Object.keys(groupedSpecies).reduce((acc, category) => {
        const filtered = groupedSpecies[category].filter(species =>
            species.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        if (filtered.length > 0) {
            acc[category] = filtered;
        }
        return acc;
    }, {});

    if (!isOpen) return null;

    return (
        <>
            {/* Card Panel - Fixed Right Sidebar */}
            <div 
                className="fixed right-0 top-0 h-screen bg-white shadow-2xl z-50 border-l border-gray-200 flex flex-col overflow-hidden max-md:w-full md:w-[600px] lg:w-[600px]"
            >
                {/* Header with Close Button */}
                <div className="relative p-4 flex-shrink-0 border-b border-gray-200 max-md:p-3">
                    <h2 className="text-lg font-bold text-gray-800 pr-8">Redigera område</h2>
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors z-10 max-md:top-3 max-md:right-3"
                    >
                        <svg className="w-7 h-7 max-md:w-6 max-md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 p-4 max-md:p-3 flex flex-col overflow-hidden">
                    {selectedZone ? (
                        <>
                            {/* Zone Info - Inline editable name */}
                            <div className="flex-shrink-0 mb-3">
                                <h3 className="text-lg font-semibold text-gray-500 uppercase tracking-wide mb-2 text-center pb-1">
                                    Områdesinformation
                                </h3>
                                {isEditingName ? (
                                    <input
                                        ref={nameInputRef}
                                        type="text"
                                        value={editedName}
                                        onChange={(e) => setEditedName(e.target.value)}
                                        onKeyDown={handleNameKeyDown}
                                        onBlur={handleNameEditComplete}
                                        className="w-full bg-white rounded-lg p-2 border-2 border-blue-500 text-lg font-medium text-gray-800 focus:outline-none text-center"
                                    />
                                ) : (
                                    <button
                                        onClick={() => setIsEditingName(true)}
                                        className="w-full bg-gray-50 rounded-lg p-2 border-2 border-gray-200 flex items-center justify-center gap-2 text-lg font-medium text-gray-800 hover:text-blue-600 transition-colors group"
                                    >
                                        <span>{editedName}</span>
                                        <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </button>
                                )}
                            </div>

                            {/* Image Display and Upload Section */}
                            <div className="flex-shrink-0 mb-3 pb-3 border-gray-200">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                    Områdesbild
                                </h3>
                                <div className="flex justify-center mb-2">
                                    {areaImage ? (
                                        <img 
                                            src={areaImage} 
                                            alt={selectedZone}
                                            className="w-48 h-auto object-cover rounded-lg"
                                        />
                                    ) : (
                                        <div className="w-48 h-28 bg-gray-100 rounded-lg flex items-center justify-center">
                                            <span className="text-gray-400 text-sm">Ingen bild tillgänglig</span>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept="image/*"
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (file && selectedZoneId) {
                                            try {
                                                //const token = await getToken();
                                                const token = localStorage.getItem("token");
                                                const formData = new FormData();
                                                formData.append('image', file);

                                                const response = await fetch(
                                                    `${process.env.REACT_APP_API_URL}/api/areas/${selectedZoneId}/images`,
                                                    {
                                                        method: 'POST',
                                                        headers: {
                                                            'Authorization': `Bearer ${token}`,
                                                        },
                                                        
                                                        body: formData
                                                    }
                                                );

                                                if (response.ok) {
                                                    // Refresh the image by re-fetching
                                                    const imageResponse = await fetch(
                                                        `${process.env.REACT_APP_API_URL}/api/areas/${selectedZoneId}/images`
                                                    );
                                                    const blob = await imageResponse.blob();
                                                    const imageUrl = URL.createObjectURL(blob);
                                                    setAreaImage(imageUrl);
                                                    alert('Bilden har laddats upp!');
                                                } else {
                                                    const errorData = await response.json();
                                                    console.error('Error uploading image:', errorData);
                                                    alert('Ett fel uppstod när bilden skulle laddas upp.');
                                                }
                                            } catch (error) {
                                                console.error('Error uploading area image:', error);
                                                alert('Ett fel uppstod när bilden skulle laddas upp.');
                                            }
                                        }
                                        // Reset the input so the same file can be selected again
                                        e.target.value = '';
                                    }}
                                    className="hidden"
                                />
                                <div className="flex justify-center">
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="w-40 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        Ändra bild
                                    </button>
                                </div>
                            </div>

                            {/* Species Checklist - Grouped by Category */}
                            <div className="border-t border-gray-200 pt-3 flex-1 flex flex-col overflow-hidden px-2 min-h-0">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex-shrink-0">
                                    Arter i området
                                </h3>
                                
                                {/* Search Filter - Fixed */}
                                <div className="flex-shrink-0 mb-2">
                                    <input
                                        type="text"
                                        placeholder="Sök arter..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    />
                                </div>

                                {/* Scrollable species list */}
                                <div 
                                    className="flex-1 overflow-y-auto overflow-x-hidden pr-2 min-h-0" 
                                    style={{ 
                                        WebkitOverflowScrolling: 'touch',
                                        scrollbarWidth: 'thin',
                                        scrollbarColor: '#9ca3af #e5e7eb'
                                    }}
                                >
                                    <div className="space-y-3 pb-3">
                                        {Object.keys(filteredGroupedSpecies).sort().map((category) => (
                                            <div key={category} className="space-y-1.5">
                                                {/* Category Header */}
                                                <h4 className="text-sm font-bold text-gray-800 bg-gray-100 px-3 py-2 rounded-lg sticky top-0 z-10">
                                                    {category}
                                                </h4>
                                                {/* Species in Category */}
                                                <div className="space-y-1 pl-2">
                                                    {filteredGroupedSpecies[category].map((species) => (
                                                        <label
                                                            key={species.id}
                                                            className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedSpeciesIds.has(species.id)}
                                                                onChange={() => handleSpeciesToggle(species.id)}
                                                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
                                                            />
                                                            <span className="text-sm text-gray-700">{species.name}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                            <p className="text-gray-500">Välj ett naturområde från listan</p>
                        </div>
                    )}
                </div>

                {/* Footer with Save and Delete Buttons */}
                {selectedZone && (
                    <div className="border-t border-gray-200 p-3 max-md:p-2.5 flex-shrink-0 bg-white">
                        <div className="flex justify-center items-center">
                            <div className="flex flex-col sm:flex-row gap-2 max-sm:gap-1.5 justify-center items-center">
                                <button
                                    onClick={handleSaveSpeciesList}
                                    className="w-64 sm:w-48 flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2.5 sm:py-1.5 rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
                                >
                                    <svg className="w-5 h-5 max-sm:w-4 max-sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Spara ändringar
                                </button>
                                <button
                                    onClick={onRemove}
                                    className="w-64 sm:w-48 flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-2.5 sm:py-1.5 rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
                                >
                                    <svg className="w-5 h-5 max-sm:w-4 max-sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Ta bort området
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
